import { createFileRoute } from '@tanstack/react-router';
import { Check, X } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/comparisons/apigee')({
  component: ApigeeComparisonPage,
  head: () => ({
    meta: [
      { title: 'Matricks vs Apigee | Matricks' },
      { name: 'description', content: 'Compare Matricks and Google Apigee for API management. See differences in features, pricing, and ease of use.' },
    ],
  }),
});

const comparisonData = [
  { feature: "API Gateway", matricks: true, apigee: true },
  { feature: "Developer Portal", matricks: true, apigee: true },
  { feature: "Analytics", matricks: true, apigee: true },
  { feature: "Pre-built Integrations", matricks: "2000+", apigee: "Limited" },
  { feature: "Visual Workflow Builder", matricks: true, apigee: false },
  { feature: "AI/LLM Gateway", matricks: true, apigee: false },
  { feature: "AI Agent Platform", matricks: true, apigee: false },
  { feature: "Multi-Cloud Support", matricks: true, apigee: "GCP Focus" },
  { feature: "No-Code Configuration", matricks: true, apigee: false },
  { feature: "Transparent Pricing", matricks: true, apigee: false },
  { feature: "Quick Setup", matricks: "Minutes", apigee: "Days/Weeks" },
];

const faqItems = [
  {
    question: "Can I use Matricks if I'm on Google Cloud?",
    answer: "Absolutely. Matricks works on any cloud platform including GCP. You get multi-cloud flexibility while still integrating seamlessly with your GCP services.",
  },
  {
    question: "How does pricing compare to Apigee?",
    answer: "Apigee pricing is complex and can be expensive, especially at scale. Matricks offers transparent, predictable pricing with all features included. Most organizations find Matricks significantly more cost-effective.",
  },
  {
    question: "Can I migrate from Apigee?",
    answer: "Yes, we support migration from Apigee. API proxies can be recreated using our visual builder, and we can run alongside Apigee during transition. Our team provides migration assistance.",
  },
];

function ApigeeComparisonPage() {
  return (
    <div>
      <ContentHeader
        title="Matricks vs Apigee"
        description="Google Apigee is an enterprise API management platform. Here's how Matricks compares for modern integration needs."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Comparisons", href: "/comparisons" },
          { label: "vs Apigee" },
        ]}
      />

      {/* Overview */}
      <ContentSection id="overview" title="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">Google Apigee</h3>
            <p className="text-muted-foreground mt-2">
              Apigee is Google's enterprise API management platform. It offers comprehensive
              API lifecycle management with strong analytics. Best suited for large enterprises
              deeply invested in the Google Cloud ecosystem.
            </p>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold text-lg">Matricks</h3>
            <p className="text-muted-foreground mt-2">
              Matricks combines API management with AI capabilities and 2000+ pre-built integrations.
              Multi-cloud by design, with a focus on rapid deployment and ease of use. Best for
              teams wanting modern capabilities without vendor lock-in.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Feature Comparison"
        background="muted"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Feature</th>
                <th className="py-4 px-4 text-center font-semibold bg-primary/5 text-primary">Matricks</th>
                <th className="py-4 px-4 text-center font-semibold">Apigee</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row) => (
                <tr key={row.feature} className="border-b last:border-0">
                  <td className="py-4 px-4">{row.feature}</td>
                  <td className="py-4 px-4 text-center bg-primary/5">
                    {row.matricks === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.matricks === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm">{row.matricks}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {row.apigee === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.apigee === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{row.apigee}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Key Differences */}
      <ContentSection id="differences" title="Key Differences">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Time to Value</h3>
            <p className="text-muted-foreground mt-2">
              Apigee implementations typically take weeks or months. Matricks gets you productive
              in minutes with pre-built integrations and visual configuration. No specialized
              training required.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">AI and Modern Capabilities</h3>
            <p className="text-muted-foreground mt-2">
              Matricks includes AI/LLM gateway and agent capabilities natively. Apigee has no
              built-in AI support. For organizations building AI-powered applications, this is
              a significant advantage.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Cloud Flexibility</h3>
            <p className="text-muted-foreground mt-2">
              Apigee is optimized for Google Cloud. Matricks is cloud-agnostic, working equally
              well on AWS, Azure, GCP, or hybrid environments. No vendor lock-in.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="See Matricks in Action"
        description="Start your free trial and experience the difference."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/info/contact" }}
        background="primary"
      />
    </div>
  );
}
